import { useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { listFriendRequests, listFriends } from './api'
import type { FriendRequest, FriendSummary } from '@/types/database'

const REQUESTS = ['friend-requests']
const FRIENDS = ['friends']

/**
 * Pending requests, shared by the tab badge and the People page.
 *
 * Through the query cache rather than each caller fetching its own copy: the
 * badge and the list have to agree, and accepting a request has to clear both
 * in the same breath.
 */
export function useFriendRequests(): { requests: FriendRequest[]; loading: boolean } {
  const { data, isPending } = useQuery({
    queryKey: REQUESTS,
    queryFn: listFriendRequests,
    staleTime: 30_000,
  })
  return { requests: data ?? [], loading: isPending }
}

export function useFriends(): { friends: FriendSummary[] | null } {
  const { data } = useQuery({ queryKey: FRIENDS, queryFn: listFriends, staleTime: 30_000 })
  return { friends: data ?? null }
}

/**
 * Call after anything that changes the graph. Accepting on a profile has to
 * move the badge and the friends list too, and those live on another screen.
 */
export function useRefreshFriends() {
  const qc = useQueryClient()
  return useCallback(async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: REQUESTS }),
      qc.invalidateQueries({ queryKey: FRIENDS }),
    ])
  }, [qc])
}
